import { Controller } from "@hotwired/stimulus"
import { GlobalWorkerOptions, getDocument } from "pdfjs-dist/build/pdf.mjs"

const ZOOM_STORAGE_KEY = "invoice_pdf_zoom_percent"

// Rasterising a page is the expensive part of showing a PDF, and pdf.js will happily do it
// on the main thread. It picks that path whenever globalThis.pdfjsWorker.WorkerMessageHandler
// is set - PDFWorker#initialize short-circuits to the fake worker before it even tries to
// spawn a real one - which is what this file used to do. The effect is not just "slower":
// the render blocks the UI thread outright, so the whole tab, both panes and every control
// freeze for the duration of a multi-page invoice.
//
// So the handler must NOT be imported into the bundle. Instead workerSrc points at the
// prebuilt worker copied into app/assets/builds by `yarn build`, and pdf.js spawns a real
// Worker. If that spawn fails for any reason, pdf.js falls back to a main-thread worker on
// its own by dynamically importing this same URL - i.e. it degrades to exactly the previous
// behaviour rather than to a broken pane. Dropping the import also takes ~2 MB out of
// application.js, which is only loaded now when a PDF is actually shown.
//
// The URL has to come from the view because Propshaft fingerprints it and there is no way
// to derive the digested name from inside the bundle.
export default class extends Controller {
  static targets = ["viewport", "zoomLabel", "status"]
  static values = {
    defaultZoom: Number,
    url: String,
    workerSrc: String,
  }

  async connect() {
    if (!this.hasUrlValue || !this.hasViewportTarget) return

    if (this.hasWorkerSrcValue && GlobalWorkerOptions.workerSrc !== this.workerSrcValue) {
      GlobalWorkerOptions.workerSrc = this.workerSrcValue
    }

    this.zoom = this.initialZoom()
    this.renderGeneration = 0
    this.pdfDocument = null

    this.updateZoomLabel()
    await this.loadAndRenderDocument()
  }

  disconnect() {
    this.renderGeneration += 1
    if (this.pdfDocument?.destroy) this.pdfDocument.destroy()
    this.pdfDocument = null
  }

  async zoomIn(event) {
    event.preventDefault()
    await this.setZoom(this.zoom + 10)
  }

  async zoomOut(event) {
    event.preventDefault()
    await this.setZoom(this.zoom - 10)
  }

  async resetZoom(event) {
    event.preventDefault()
    await this.setZoom(100)
  }

  initialZoom() {
    const persistedZoom = this.readPersistedZoom()
    if (persistedZoom !== null) return persistedZoom

    if (this.hasDefaultZoomValue && Number.isFinite(this.defaultZoomValue)) {
      return this.clampZoom(this.defaultZoomValue)
    }

    return 110
  }

  clampZoom(value) {
    return Math.max(50, Math.min(300, value))
  }

  async setZoom(value) {
    const normalized = this.clampZoom(Math.round(value / 5) * 5)
    if (normalized === this.zoom) return

    this.zoom = normalized
    this.persistZoom(normalized)
    this.updateZoomLabel()

    if (!this.pdfDocument) return

    await this.renderDocumentPages()
  }

  updateZoomLabel() {
    if (!this.hasZoomLabelTarget) return

    this.zoomLabelTarget.textContent = `${this.zoom}%`
  }

  async loadAndRenderDocument() {
    try {
      this.setStatus("Loading PDF preview...")
      const loadingTask = getDocument({
        url: this.urlValue,
        withCredentials: true,
      })
      this.pdfDocument = await loadingTask.promise
      await this.renderDocumentPages()
      this.setStatus("")
    } catch (error) {
      console.error("PDF preview failed to load", error)
      this.setStatus("Could not load PDF.js preview.")
    }
  }

  async renderDocumentPages() {
    if (!this.pdfDocument || !this.hasViewportTarget) return

    const generation = ++this.renderGeneration
    this.setStatus(`Rendering at ${this.zoom}%...`)
    this.viewportTarget.replaceChildren()

    const renderedPages = []

    for (let pageNumber = 1; pageNumber <= this.pdfDocument.numPages; pageNumber += 1) {
      if (generation !== this.renderGeneration) return

      const page = await this.pdfDocument.getPage(pageNumber)
      if (generation !== this.renderGeneration) return

      const { element, viewport } = await this.renderPage(page)
      if (generation !== this.renderGeneration) return

      this.viewportTarget.appendChild(element)
      renderedPages.push({ pageNumber, page, viewport, element })
    }

    if (generation !== this.renderGeneration) return

    this.setStatus("")

    // Announce the freshly rendered pages so sibling controllers can hang overlays off
    // them. Fires after every render, including zoom changes, because zoom replaces the
    // page elements and anything anchored to them has to be rebuilt.
    this.dispatch("rendered", { detail: { pages: renderedPages } })
  }

  async renderPage(page) {
    const viewport = page.getViewport({ scale: this.zoom / 100 })
    const outputScale = window.devicePixelRatio || 1

    const wrapper = document.createElement("div")
    wrapper.className = "invoice-pdf__page"

    const canvas = document.createElement("canvas")
    canvas.className = "invoice-pdf__canvas"
    canvas.width = Math.floor(viewport.width * outputScale)
    canvas.height = Math.floor(viewport.height * outputScale)
    canvas.style.width = `${Math.floor(viewport.width)}px`
    canvas.style.height = `${Math.floor(viewport.height)}px`

    const context = canvas.getContext("2d", { alpha: false })
    const transform = outputScale === 1 ? null : [outputScale, 0, 0, outputScale, 0, 0]

    await page.render({
      canvasContext: context,
      viewport,
      transform,
    }).promise

    wrapper.appendChild(canvas)
    return { element: wrapper, viewport }
  }

  setStatus(message) {
    if (!this.hasStatusTarget) return

    this.statusTarget.textContent = message
    this.statusTarget.classList.toggle("invoice-is-hidden", message === "")
  }

  readPersistedZoom() {
    try {
      const raw = window.localStorage.getItem(ZOOM_STORAGE_KEY)
      if (!raw) return null

      const parsed = Number.parseInt(raw, 10)
      if (!Number.isFinite(parsed)) return null

      return this.clampZoom(parsed)
    } catch {
      return null
    }
  }

  persistZoom(value) {
    try {
      window.localStorage.setItem(ZOOM_STORAGE_KEY, String(value))
    } catch {
      // Ignore storage errors (private browsing, storage disabled, etc.)
    }
  }
}
